import React, { useCallback, useMemo } from 'react';

import { useIsMobileV2 } from 'src/application/hooks/useIsMobile';

import { ItemValidator } from '../../../../../services/ItemValidator';
import { goToSelectedSequencedFlowContentItem } from '../../../../actions/contentItemActions';
import { DeselectedMenuItemAction } from '../../../../actions/menuActions';
import { useStudentDispatch } from '../../../../hooks/useStudentDispatch';
import { HistoryItem } from '../../../../store/reducers/currentSequencedFlowPositionReducer';
import { getItemByIdTypedSelector } from '../../../../store/reducers/items/itemsSelectors';
import { useSelectItem } from '../../../../store/reducers/items/useSelectItem';
import { addStartOfItem } from '../../../../store/reducers/student/tracking/itemsTrackingActions';
import { noTrackedItemsSelector } from '../../../../store/reducers/student/tracking/itemsTrackingSelectors';
import { getCurrentItem } from '../../../../store/selectors/getCurrentItem';
import { getMenuContentItemsSelector } from '../../../../store/selectors/getMenuContentItems';
import { getSequencedFlowStructureSelector } from '../../../../store/selectors/getSequencedFlowStructureSelector';
import { useStudentSelector } from '../../../../store/selectors/useStudentSelector';
import { AssertionException } from '../../../../utils/log';
import { calculateHistoryItems } from '../../../SequencedFlow/utils';
import { userProgressServiceSelector } from '../../../tracking/services/UserProgressService';
import { ItemDetailsType, MenuItem } from '../../components/student/MenuItem';
import { isSection, SequencedFlowItem as SequencedFlowItemModel, TrackingStatus } from '@ael/core-api';
import { createCompletedPercentageDisplayConfiguration } from '@ael/shared-components-library';

interface OwnProps {
  item: SequencedFlowItemModel;
  trackingStatus?: TrackingStatus;
  children: React.ReactNode;
  parentItemId: string | null;
  nestedLevel: number;
  isLinearModeOn: boolean;
}

export const SequencedFlowItem: React.FC<OwnProps> = React.memo((ownProps) => {
  const dispatch = useStudentDispatch();
  const { goToItem } = useSelectItem();
  const currentItem = useStudentSelector(getCurrentItem);
  const getItemByIdTyped = useStudentSelector(getItemByIdTypedSelector);
  const getSequencedFlowStructure = useStudentSelector(getSequencedFlowStructureSelector);
  const getMenuContentItems = useStudentSelector(getMenuContentItemsSelector);
  const isMobile = useIsMobileV2();

  const currentSection = useStudentSelector((state) =>
    state.currentSequencedFlowPosition.currentSection
      ? getItemByIdTyped(state.currentSequencedFlowPosition.currentSection, isSection)
      : null
  );

  const itemInProgress = currentItem?.id === ownProps.item.id ? currentSection ?? ownProps.item : ownProps.item;

  const progressPercentage = useStudentSelector((state) => {
    const userProgressService = userProgressServiceSelector(state);
    return userProgressService.evaluateSequencedFlow(itemInProgress, ownProps.item);
  });

  const isItemMandatoryOrCompletionDisabled = useStudentSelector(noTrackedItemsSelector) || ownProps.item.isMandatory;

  const handleItemClick = useCallback(() => {
    dispatch(addStartOfItem(ownProps.item.id));
    goToItem(ownProps.item.id);
  }, [dispatch, goToItem, ownProps.item.id]);

  const handleMenuContentItemClick = useCallback(
    (contentItemId: string) => {
      const sequencedFlowStructure = getSequencedFlowStructure(ownProps.item.id);
      if (!sequencedFlowStructure) {
        throw AssertionException('Sequenced flow structure cannot be undefined.');
      }
      const currentHistoryItem = sequencedFlowStructure.find(
        (historyItem: HistoryItem) => historyItem?.contentItemId === contentItemId
      );
      if (!currentHistoryItem) {
        throw AssertionException('CurrentHistoryItem cannot be undefined.');
      }
      const history = calculateHistoryItems(sequencedFlowStructure, currentHistoryItem);
      dispatch(
        goToSelectedSequencedFlowContentItem(
          currentHistoryItem.contentItemId,
          currentHistoryItem.sectionId,
          isMobile,
          history
        )
      );
    },
    [dispatch, getSequencedFlowStructure, isMobile, ownProps.item.id]
  );

  const handleDeselectMenuItem = useCallback(() => {
    dispatch(DeselectedMenuItemAction(ownProps.item));
  }, [dispatch, ownProps.item]);

  const isSelected = currentItem === ownProps.item;
  const isValid = useMemo(() => ItemValidator.isValid(ownProps.item), [ownProps.item]);

  const menuItemDetails = useMemo(
    () => ({
      itemDetailsType: ItemDetailsType.Item as const,
      isSelected,
      active: isSelected,
      trackingStatus: ownProps.trackingStatus,
      disabled: !isValid,
      contentItems: getMenuContentItems(ownProps.item.id),
      progress: isValid ? createCompletedPercentageDisplayConfiguration(progressPercentage) : undefined,
      showItemCompletion: isItemMandatoryOrCompletionDisabled,
      parentItemId: ownProps.parentItemId,
      nestedLevel: ownProps.nestedLevel,
      itemId: ownProps.item.id,
    }),
    [
      isSelected,
      ownProps.trackingStatus,
      ownProps.item.id,
      ownProps.parentItemId,
      ownProps.nestedLevel,
      isValid,
      getMenuContentItems,
      progressPercentage,
      isItemMandatoryOrCompletionDisabled,
    ]
  );

  const handleClick = isSelected ? handleDeselectMenuItem : handleItemClick;

  const mergeProps = useMemo(
    () => ({
      children: ownProps.children,
      menuItemDetails,
      isLinearModeOn: ownProps.isLinearModeOn,
      onClick: handleClick,
      onMenuContentItemClick: handleMenuContentItemClick,
    }),
    [ownProps.children, ownProps.isLinearModeOn, menuItemDetails, handleClick, handleMenuContentItemClick]
  );

  return <MenuItem {...mergeProps} />;
});
